import * as i1 from '@angular/common';
import { CommonModule, NgClass, NgTemplateOutlet } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, ViewChild, Output, Input, HostBinding, ViewEncapsulation, Component, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { WindmillContextMenuDirective, WindmillContextMenuItemDirective, WindmillContextMenuListComponent, WindmillContextMenuModule } from '@windmill/ng-windmill/context-menu';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';

const HIGH_EMPHASIS_THEME = 'high-emphasis-theme';
const HIGH_EMPHASIS_DANGER = 'high-emphasis-danger';
const ICON_HIGH_EMPHASIS_THEME = 'icon-high-emphasis-theme';
const ICON_HIGH_EMPHASIS_DANGER = 'icon-high-emphasis-danger';
const ICON_HIGH_EMPHASIS_WARNING = 'icon-high-emphasis-warning';
const ICON_HIGH_EMPHASIS_SUCCESS = 'icon-high-emphasis-success';
const ICON_STATUS_HIGH_EMPHASIS_THEME = 'icon-status-high-emphasis-theme';
const ICON_STATUS_HIGH_EMPHASIS_WARNING = 'icon-status-high-emphasis-warning';
const ICON_STATUS_HIGH_EMPHASIS_DANGER = 'icon-status-high-emphasis-danger';
const ICON_STATUS_HIGH_EMPHASIS_SUCCESS = 'icon-status-high-emphasis-success';
const ICON_LOW_EMPHASIS = 'icon-low-emphasis';
const ICON_OUTLINED_THEME = 'icon-outlined-theme';
const ICON_OUTLINED_DANGER = 'icon-outlined-danger';
const ICON_GHOST_THEME = 'icon-ghost-theme';
const ICON_GHOST_GREYSCALE = 'icon-ghost-greyscale';
const INACTIVE_THEME = '';
const ICON_BUTTON_THEMES = [
    ICON_HIGH_EMPHASIS_THEME,
    ICON_HIGH_EMPHASIS_WARNING,
    ICON_HIGH_EMPHASIS_DANGER,
    ICON_HIGH_EMPHASIS_SUCCESS,
    ICON_STATUS_HIGH_EMPHASIS_THEME,
    ICON_STATUS_HIGH_EMPHASIS_WARNING,
    ICON_STATUS_HIGH_EMPHASIS_DANGER,
    ICON_STATUS_HIGH_EMPHASIS_SUCCESS,
    ICON_LOW_EMPHASIS,
    ICON_OUTLINED_THEME,
    ICON_OUTLINED_DANGER,
    ICON_GHOST_THEME,
    ICON_GHOST_GREYSCALE
];

const ACTIVE_THEME_MAP = {
    'low-emphasis': HIGH_EMPHASIS_THEME,
    'outlined-theme': HIGH_EMPHASIS_THEME,
    'outlined-danger': HIGH_EMPHASIS_DANGER,
    'ghost-theme': HIGH_EMPHASIS_THEME,
    'ghost-greyscale': HIGH_EMPHASIS_THEME,
    'icon-low-emphasis': ICON_HIGH_EMPHASIS_THEME,
    'icon-outlined-theme': ICON_HIGH_EMPHASIS_THEME,
    'icon-outlined-danger': ICON_HIGH_EMPHASIS_DANGER,
    'icon-ghost-theme': ICON_HIGH_EMPHASIS_THEME,
    'icon-ghost-greyscale': ICON_HIGH_EMPHASIS_THEME
};
const SWITCH_THEME_MAP = {
    'high-emphasis-theme': ICON_HIGH_EMPHASIS_THEME,
    'icon-high-emphasis-theme': HIGH_EMPHASIS_THEME,
    'high-emphasis-danger': ICON_HIGH_EMPHASIS_DANGER,
    'icon-high-emphasis-danger': HIGH_EMPHASIS_DANGER,
    '': INACTIVE_THEME
};

const windmillComponents = [
    CentricIcon2Component,
    WindmillContextMenuDirective,
    WindmillContextMenuItemDirective,
    WindmillContextMenuListComponent
];
/**
 * To use this component in a module, you can import the `WindmillComboButtonModule`:
 * @example
 * ```typescript
 * import { WindmillComboButtonModule } from '@windmill/ng-windmill/combo-button';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillComboButtonComponent`:
 * @example
 * ```typescript
 * import { WindmillComboButtonComponent } from '@windmill/ng-windmill/combo-button';
 * ```
 */
class WindmillComboButtonComponent {
    /**
     * Property that sets the menu items
     */
    menuItems;
    /**
     * Property that sets the content button type
     */
    contentType = 'dropdown';
    /**
     * Combo button size
     */
    size = 'large';
    /**
     * If true, the combo button will be disabled
     */
    disabled = false;
    /**
     * Used for displaying the button text for dropdown content type
     */
    dropdownButtonText;
    /**
     * Dropdown content type button icon
     */
    dropdownIconId;
    /**
     * Property that sets the type of the dropdown combo button from a design perspective
     */
    dropdownType = HIGH_EMPHASIS_THEME;
    /**
     * Property that sets the type of the actions-split combo button from a design perspective
     */
    splitActionsType = HIGH_EMPHASIS_THEME;
    /**
     * Property that sets the type of the persistent-split combo button from a design perspective
     */
    splitPersistentType = HIGH_EMPHASIS_THEME;
    /**
     * Property that sets the type of the persistent-split-active combo button from a design perspective
     */
    set splitPersistentActiveType(newValue) {
        if ((this._splitPersistentActiveType?.includes('icon') && !newValue?.includes('icon')) ||
            (!this._splitPersistentActiveType?.includes('icon') && newValue?.includes('icon'))) {
            this._switchSplitPersistentActiveType();
            this._updateType();
        }
        this._splitPersistentActiveType = newValue;
        this._activatePersistentActiveType();
    }
    get splitPersistentActiveType() {
        return this._splitPersistentActiveType;
    }
    /**
     * Property that sets the active state of the split persistent active button
     */
    get isActive() {
        return this._isActive;
    }
    set isActive(value) {
        this._isActive = this.isPersistentSplitActiveContentType ? value : false;
        if (this._isActive && this.isPersistentSplitActiveContentType) {
            this._activatePersistentActiveType();
        }
        else {
            this.activeTheme = INACTIVE_THEME;
        }
    }
    /**
     * Sets the id attribute on the action button
     */
    buttonId;
    /**
     * Sets the data-testid attribute on the action button
     */
    testButtonId;
    /**
     * Sets the id attribute on the split arrow button
     */
    splitArrowButtonId;
    /**
     * Sets the data-testid attribute on the split arrow button
     */
    testSplitArrowButtonId;
    /**
     * Sets the id attribute on the list
     */
    listId;
    /**
     * Sets the data-testid attribute on the list
     */
    listDataTestId;
    /**
     * Aria label for action button
     */
    ariaLabel;
    /**
     * Aria labelledby for action button
     */
    ariaLabelledby;
    /**
     * Aria description for action button
     */
    ariaDescription;
    /**
     * Aria describedby for action button
     */
    ariaDescribedby;
    /**
     * Aria label for split arrow button
     */
    ariaLabelSplitArrow;
    /**
     * Aria label for split arrow button
     */
    ariaLabelledbySplitArrow;
    /**
     * Aria label for split arrow button
     */
    ariaDescriptionSplitArrow;
    /**
     * Aria label for split arrow button
     */
    ariaDescribedbySplitArrow;
    /**
     * Emits a WindmillComboButtonMenuItem object when the action split button is clicked
     */
    actionSelected = new EventEmitter();
    /**
     * Emits a boolean when the list overlay is opened or closed
     */
    openedListChange = new EventEmitter(false);
    /**
     * Emits a WindmillComboButtonMenuItem object when a menu item is selected
     */
    menuItemSelected = new EventEmitter();
    triggerButton;
    contextMenuDirective;
    ariaHasPopup = true;
    isListOpened = false;
    splitIconIdValue = '';
    splitButtonTextValue = '';
    typeValue = HIGH_EMPHASIS_THEME;
    _ariaPressed = false;
    _splitPersistentActiveType = 'low-emphasis';
    _persistentSplitMainAction;
    _persistentSplitActiveMainAction;
    _activeTheme = '';
    _isActive = false;
    get isDropdownContentType() {
        return this.contentType === 'dropdown';
    }
    get isActionsSplitContentType() {
        return this.contentType === 'actions-split';
    }
    get isPersistentSplitContentType() {
        return this.contentType === 'persistent-split';
    }
    get isPersistentSplitActiveContentType() {
        return this.contentType === 'persistent-split-active';
    }
    get comboButtonClass() {
        return `${this.contentType} ${this.size}`;
    }
    get isIconButton() {
        return ICON_BUTTON_THEMES.includes(this.typeValue);
    }
    get buttonClass() {
        return `${this.typeValue} ${this.size}`;
    }
    get iconSize() {
        return this.size === 'small' ? '20' : '24';
    }
    get isSplitContentType() {
        return this.isActionsSplitContentType || this.isPersistentSplitContentType || this.isPersistentSplitActiveContentType;
    }
    get isSplitButtonOutlinedType() {
        return (this.isSplitContentType &&
            (this.typeValue === 'ghost-theme' ||
                this.typeValue === 'ghost-greyscale' ||
                this.typeValue === 'icon-ghost-theme' ||
                this.typeValue === 'icon-ghost-greyscale'));
    }
    get contentMenuItems() {
        return this._menuItemsValue?.filter(item => !item.isFooter);
    }
    get footerMenuItems() {
        return this._menuItemsValue?.filter(item => item.isFooter);
    }
    get ariaPressed() {
        return this.isPersistentSplitActiveContentType ? this._ariaPressed : null;
    }
    set ariaPressed(value) {
        this._ariaPressed = this.isPersistentSplitActiveContentType && !this.disabled ? value : null;
    }
    get activeTheme() {
        return this._activeTheme;
    }
    set activeTheme(value) {
        this._activeTheme = value;
        this.ariaPressed = this._activeTheme !== INACTIVE_THEME;
    }
    get _contentMenuAvailableItems() {
        return this.menuItems && Array.isArray(this.menuItems) ? this.menuItems?.filter(item => !item.isDisabled) : [];
    }
    get _menuItemsValue() {
        if (this.isActionsSplitContentType) {
            return this.menuItems?.slice(1);
        }
        return this.menuItems;
    }
    ngOnInit() {
        this._persistentSplitMainAction = this._contentMenuAvailableItems[0];
        this._persistentSplitActiveMainAction = this._contentMenuAvailableItems[0];
        this._updateSplitIconAndText();
    }
    ngOnChanges(changes) {
        if (changes.contentType || changes.menuItems) {
            this._updateSplitIconAndText();
        }
        if (this._hasTypeChanges(changes) || changes.isActive) {
            this._updateType();
        }
    }
    isMenuItemSelected(item) {
        if (this.isPersistentSplitContentType) {
            return item === this._persistentSplitMainAction;
        }
        else if (this.isPersistentSplitActiveContentType) {
            return item === this._persistentSplitActiveMainAction;
        }
        return false;
    }
    isMenuItemFooter(item) {
        return Boolean(item.isFooter);
    }
    isMenuItemDisabled(item) {
        return Boolean(item.isDisabled);
    }
    hasDisabledAttribute(item) {
        return item.isDisabled ? true : null;
    }
    onActionSplitButtonClick() {
        let emittedItem = this._contentMenuAvailableItems[0];
        if (this.isPersistentSplitContentType) {
            emittedItem = this._persistentSplitMainAction;
        }
        else if (this.isPersistentSplitActiveContentType) {
            emittedItem = this._persistentSplitActiveMainAction;
            this._updateType();
        }
        this.actionSelected.emit(emittedItem);
    }
    onOpenedListChange(isOpen) {
        this.openedListChange.emit(isOpen);
        this.isListOpened = isOpen;
    }
    onMenuItemKeyDown(event, item) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Space' || event.key === 'Spacebar') {
            event.preventDefault();
            this._onMenuItemSelected(item);
            this._closeOverlay(true);
        }
    }
    onMenuItemClick(item) {
        this._onMenuItemSelected(item);
        this._closeOverlay();
    }
    _hasTypeChanges(changes) {
        return Boolean(changes.contentType ||
            changes.dropdownType ||
            changes.splitActionsType ||
            changes.splitPersistentType ||
            changes.splitPersistentActiveType);
    }
    _updateSplitIconAndText() {
        if (this.isPersistentSplitContentType) {
            this._setValuesForSplitIconAndText(this._persistentSplitMainAction);
        }
        else if (this.isPersistentSplitActiveContentType) {
            this._setValuesForSplitIconAndText(this._persistentSplitActiveMainAction);
        }
        else if (this.isActionsSplitContentType && this._contentMenuAvailableItems.length > 0) {
            this._setValuesForSplitIconAndText(this._contentMenuAvailableItems[0]);
        }
    }
    _setValuesForSplitIconAndText(mainAction) {
        this.splitIconIdValue = mainAction?.iconId ? mainAction.iconId : '';
        this.splitButtonTextValue = mainAction?.title;
    }
    _updateType() {
        if (this.isDropdownContentType) {
            this.typeValue = this.dropdownType;
        }
        else {
            this._updateSplitType();
        }
    }
    _updateSplitType() {
        if (this.isActionsSplitContentType) {
            this.typeValue = this.splitActionsType;
        }
        else if (this.isPersistentSplitContentType) {
            this.typeValue = this.splitPersistentType;
        }
        else if (this.isPersistentSplitActiveContentType) {
            this.typeValue = this.activeTheme ? this.activeTheme : this.splitPersistentActiveType;
        }
    }
    _onMenuItemSelected(item) {
        this._updateMainAction(item);
        this.menuItemSelected.emit(item);
        if (!this.isPersistentSplitContentType) {
            this.actionSelected.emit(item);
        }
    }
    _updateMainAction(item) {
        if (this.isPersistentSplitContentType) {
            this._persistentSplitMainAction = item;
            this._setValuesForSplitIconAndText(this._persistentSplitMainAction);
        }
        else if (this.isPersistentSplitActiveContentType) {
            this._persistentSplitActiveMainAction = item;
            this._setValuesForSplitIconAndText(this._persistentSplitActiveMainAction);
            this._activatePersistentActiveType();
            this._updateType();
        }
    }
    _closeOverlay(shouldFocusTriggerButton) {
        this.contextMenuDirective.closeOverlay(shouldFocusTriggerButton);
    }
    _activatePersistentActiveType() {
        if (this.isActive && this.isPersistentSplitActiveContentType) {
            this.activeTheme = this._getActiveThemeFor(this.splitPersistentActiveType);
        }
    }
    _getActiveThemeFor(currentTheme) {
        return ACTIVE_THEME_MAP[currentTheme];
    }
    _switchSplitPersistentActiveType() {
        this.activeTheme = SWITCH_THEME_MAP[this.activeTheme];
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillComboButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillComboButtonComponent, isStandalone: true, selector: "windmill-combo-button", inputs: { menuItems: "menuItems", contentType: "contentType", size: "size", disabled: "disabled", dropdownButtonText: "dropdownButtonText", dropdownIconId: "dropdownIconId", dropdownType: "dropdownType", splitActionsType: "splitActionsType", splitPersistentType: "splitPersistentType", splitPersistentActiveType: "splitPersistentActiveType", isActive: "isActive", buttonId: "buttonId", testButtonId: "testButtonId", splitArrowButtonId: "splitArrowButtonId", testSplitArrowButtonId: "testSplitArrowButtonId", listId: "listId", listDataTestId: "listDataTestId", ariaLabel: "ariaLabel", ariaLabelledby: "ariaLabelledby", ariaDescription: "ariaDescription", ariaDescribedby: "ariaDescribedby", ariaLabelSplitArrow: "ariaLabelSplitArrow", ariaLabelledbySplitArrow: "ariaLabelledbySplitArrow", ariaDescriptionSplitArrow: "ariaDescriptionSplitArrow", ariaDescribedbySplitArrow: "ariaDescribedbySplitArrow" }, outputs: { actionSelected: "actionSelected", openedListChange: "openedListChange", menuItemSelected: "menuItemSelected" }, host: { properties: { "class.is-disabled": "this.disabled" } }, viewQueries: [{ propertyName: "triggerButton", first: true, predicate: ["triggerButton"], descendants: true }, { propertyName: "contextMenuDirective", first: true, predicate: WindmillContextMenuDirective, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\r\n  class=\"windmill-combo-button\"\r\n  [class.is-icon-button]=\"isIconButton\"\r\n  [ngClass]=\"comboButtonClass\">\r\n  @if (isDropdownContentType) {\r\n    <button\r\n      #triggerButton\r\n      [windmill-context-menu]=\"menuTemplate\"\r\n      class=\"centric-inner-button dropdown-combo-button\"\r\n      [class.disabled]=\"disabled\"\r\n      [ngClass]=\"buttonClass\"\r\n      [disabled]=\"disabled\"\r\n      [attr.id]=\"buttonId\"\r\n      [attr.data-testid]=\"testButtonId\"\r\n      [attr.aria-haspopup]=\"ariaHasPopup\"\r\n      [attr.aria-expanded]=\"isListOpened\"\r\n      [attr.aria-controls]=\"listId\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\">\r\n      @if (dropdownIconId) {\r\n        <centric-icon2\r\n          class=\"content-icon\"\r\n          [svgIcon]=\"dropdownIconId\"\r\n          [size]=\"iconSize\">\r\n        </centric-icon2>\r\n      }\r\n      @if (dropdownButtonText && !isIconButton) {\r\n        <span class=\"combo-button-text\">\r\n          {{ dropdownButtonText }}\r\n        </span>\r\n      }\r\n      <ng-container *ngTemplateOutlet=\"arrowIcon\"></ng-container>\r\n    </button>\r\n  }\r\n  @if (isSplitContentType) {\r\n    <button\r\n      class=\"centric-inner-button split-combo-button\"\r\n      [class.disabled]=\"disabled\"\r\n      [ngClass]=\"buttonClass\"\r\n      [disabled]=\"disabled\"\r\n      [attr.id]=\"buttonId\"\r\n      [attr.data-testid]=\"testButtonId\"\r\n      [attr.aria-pressed]=\"disabled ? null : ariaPressed\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\"\r\n      (click)=\"onActionSplitButtonClick()\">\r\n      @if (splitIconIdValue) {\r\n        <centric-icon2\r\n          class=\"content-icon\"\r\n          [svgIcon]=\"splitIconIdValue\"\r\n          [size]=\"iconSize\">\r\n        </centric-icon2>\r\n      }\r\n      @if (!isIconButton) {\r\n        <span class=\"combo-button-text\">\r\n          {{ splitButtonTextValue }}\r\n        </span>\r\n      }\r\n    </button>\r\n    @if (isSplitButtonOutlinedType) {\r\n      <div\r\n        class=\"split-divider-ghost\"\r\n        [class.disabled]=\"disabled\"></div>\r\n    }\r\n    <button\r\n      #triggerButton\r\n      [windmill-context-menu]=\"menuTemplate\"\r\n      class=\"centric-inner-button split-arrow-combo-button\"\r\n      [class.disabled]=\"disabled\"\r\n      [ngClass]=\"buttonClass\"\r\n      [disabled]=\"disabled\"\r\n      [attr.id]=\"splitArrowButtonId\"\r\n      [attr.data-testid]=\"testSplitArrowButtonId\"\r\n      [attr.aria-haspopup]=\"ariaHasPopup\"\r\n      [attr.aria-expanded]=\"isListOpened\"\r\n      [attr.aria-controls]=\"listId\"\r\n      [attr.aria-label]=\"ariaLabelSplitArrow\"\r\n      [attr.aria-labelledby]=\"ariaLabelledbySplitArrow\"\r\n      [attr.aria-description]=\"ariaDescriptionSplitArrow\"\r\n      [attr.aria-describedby]=\"ariaDescribedbySplitArrow\">\r\n      <ng-container *ngTemplateOutlet=\"arrowIcon\"></ng-container>\r\n    </button>\r\n  }\r\n</div>\r\n\r\n<ng-template #arrowIcon>\r\n  <div class=\"arrow-icon-wrapper\">\r\n    <centric-icon2\r\n      class=\"arrow-icon\"\r\n      [svgIcon]=\"'caret-small-down_b'\"\r\n      [size]=\"iconSize\">\r\n    </centric-icon2>\r\n  </div>\r\n</ng-template>\r\n\r\n<ng-template #menuTemplate>\r\n  @if (menuItems && menuItems.length > 0) {\r\n    <windmill-context-menu-list\r\n      [listId]=\"listId\"\r\n      [listDataTestId]=\"listDataTestId\"\r\n      (openedListChange)=\"onOpenedListChange($event)\">\r\n      @for (contentItem of contentMenuItems; track contentItem) {\r\n        <button\r\n          windmill-context-menu-item\r\n          [isSelected]=\"isMenuItemSelected(contentItem)\"\r\n          [isDisabled]=\"isMenuItemDisabled(contentItem)\"\r\n          [attr.disabled]=\"hasDisabledAttribute(contentItem)\"\r\n          (keydown)=\"onMenuItemKeyDown($event, contentItem)\"\r\n          (click)=\"onMenuItemClick(contentItem)\">\r\n          @if (contentItem.iconId) {\r\n            <centric-icon2\r\n              [svgIcon]=\"contentItem.iconId\"\r\n              [size]=\"'20'\"></centric-icon2>\r\n          }\r\n          {{ contentItem.title }}\r\n        </button>\r\n      }\r\n      @for (footerItem of footerMenuItems; track footerItem) {\r\n        <button\r\n          windmill-context-menu-item\r\n          [isSelected]=\"isMenuItemSelected(footerItem)\"\r\n          [isFooter]=\"isMenuItemFooter(footerItem)\"\r\n          [isDisabled]=\"isMenuItemDisabled(footerItem)\"\r\n          [attr.disabled]=\"hasDisabledAttribute(footerItem)\"\r\n          (keydown)=\"onMenuItemKeyDown($event, footerItem)\"\r\n          (click)=\"onMenuItemClick(footerItem)\">\r\n          @if (footerItem.iconId) {\r\n            <centric-icon2\r\n              [svgIcon]=\"footerItem.iconId\"\r\n              [size]=\"'20'\"></centric-icon2>\r\n          }\r\n          {{ footerItem.title }}\r\n        </button>\r\n      }\r\n    </windmill-context-menu-list>\r\n  }\r\n</ng-template>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "ngmodule", type: FormsModule }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }, { kind: "directive", type: WindmillContextMenuDirective, selector: "[windmill-context-menu]", inputs: ["windmill-context-menu"] }, { kind: "directive", type: WindmillContextMenuItemDirective, selector: "[windmill-context-menu-item]", inputs: ["isSelected", "isDisabled", "isFooter", "role"], exportAs: ["windmill-context-menu-item"] }, { kind: "component", type: WindmillContextMenuListComponent, selector: "windmill-context-menu-list", inputs: ["listId", "listDataTestId", "listMaxHeight"], outputs: ["openedListChange"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillComboButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-combo-button', encapsulation: ViewEncapsulation.None, imports: [CommonModule, FormsModule, NgClass, NgTemplateOutlet, ...windmillComponents], standalone: true, template: "<div\r\n  class=\"windmill-combo-button\"\r\n  [class.is-icon-button]=\"isIconButton\"\r\n  [ngClass]=\"comboButtonClass\">\r\n  @if (isDropdownContentType) {\r\n    <button\r\n      #triggerButton\r\n      [windmill-context-menu]=\"menuTemplate\"\r\n      class=\"centric-inner-button dropdown-combo-button\"\r\n      [class.disabled]=\"disabled\"\r\n      [ngClass]=\"buttonClass\"\r\n      [disabled]=\"disabled\"\r\n      [attr.id]=\"buttonId\"\r\n      [attr.data-testid]=\"testButtonId\"\r\n      [attr.aria-haspopup]=\"ariaHasPopup\"\r\n      [attr.aria-expanded]=\"isListOpened\"\r\n      [attr.aria-controls]=\"listId\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\">\r\n      @if (dropdownIconId) {\r\n        <centric-icon2\r\n          class=\"content-icon\"\r\n          [svgIcon]=\"dropdownIconId\"\r\n          [size]=\"iconSize\">\r\n        </centric-icon2>\r\n      }\r\n      @if (dropdownButtonText && !isIconButton) {\r\n        <span class=\"combo-button-text\">\r\n          {{ dropdownButtonText }}\r\n        </span>\r\n      }\r\n      <ng-container *ngTemplateOutlet=\"arrowIcon\"></ng-container>\r\n    </button>\r\n  }\r\n  @if (isSplitContentType) {\r\n    <button\r\n      class=\"centric-inner-button split-combo-button\"\r\n      [class.disabled]=\"disabled\"\r\n      [ngClass]=\"buttonClass\"\r\n      [disabled]=\"disabled\"\r\n      [attr.id]=\"buttonId\"\r\n      [attr.data-testid]=\"testButtonId\"\r\n      [attr.aria-pressed]=\"disabled ? null : ariaPressed\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\"\r\n      (click)=\"onActionSplitButtonClick()\">\r\n      @if (splitIconIdValue) {\r\n        <centric-icon2\r\n          class=\"content-icon\"\r\n          [svgIcon]=\"splitIconIdValue\"\r\n          [size]=\"iconSize\">\r\n        </centric-icon2>\r\n      }\r\n      @if (!isIconButton) {\r\n        <span class=\"combo-button-text\">\r\n          {{ splitButtonTextValue }}\r\n        </span>\r\n      }\r\n    </button>\r\n    @if (isSplitButtonOutlinedType) {\r\n      <div\r\n        class=\"split-divider-ghost\"\r\n        [class.disabled]=\"disabled\"></div>\r\n    }\r\n    <button\r\n      #triggerButton\r\n      [windmill-context-menu]=\"menuTemplate\"\r\n      class=\"centric-inner-button split-arrow-combo-button\"\r\n      [class.disabled]=\"disabled\"\r\n      [ngClass]=\"buttonClass\"\r\n      [disabled]=\"disabled\"\r\n      [attr.id]=\"splitArrowButtonId\"\r\n      [attr.data-testid]=\"testSplitArrowButtonId\"\r\n      [attr.aria-haspopup]=\"ariaHasPopup\"\r\n      [attr.aria-expanded]=\"isListOpened\"\r\n      [attr.aria-controls]=\"listId\"\r\n      [attr.aria-label]=\"ariaLabelSplitArrow\"\r\n      [attr.aria-labelledby]=\"ariaLabelledbySplitArrow\"\r\n      [attr.aria-description]=\"ariaDescriptionSplitArrow\"\r\n      [attr.aria-describedby]=\"ariaDescribedbySplitArrow\">\r\n      <ng-container *ngTemplateOutlet=\"arrowIcon\"></ng-container>\r\n    </button>\r\n  }\r\n</div>\r\n\r\n<ng-template #arrowIcon>\r\n  <div class=\"arrow-icon-wrapper\">\r\n    <centric-icon2\r\n      class=\"arrow-icon\"\r\n      [svgIcon]=\"'caret-small-down_b'\"\r\n      [size]=\"iconSize\">\r\n    </centric-icon2>\r\n  </div>\r\n</ng-template>\r\n\r\n<ng-template #menuTemplate>\r\n  @if (menuItems && menuItems.length > 0) {\r\n    <windmill-context-menu-list\r\n      [listId]=\"listId\"\r\n      [listDataTestId]=\"listDataTestId\"\r\n      (openedListChange)=\"onOpenedListChange($event)\">\r\n      @for (contentItem of contentMenuItems; track contentItem) {\r\n        <button\r\n          windmill-context-menu-item\r\n          [isSelected]=\"isMenuItemSelected(contentItem)\"\r\n          [isDisabled]=\"isMenuItemDisabled(contentItem)\"\r\n          [attr.disabled]=\"hasDisabledAttribute(contentItem)\"\r\n          (keydown)=\"onMenuItemKeyDown($event, contentItem)\"\r\n          (click)=\"onMenuItemClick(contentItem)\">\r\n          @if (contentItem.iconId) {\r\n            <centric-icon2\r\n              [svgIcon]=\"contentItem.iconId\"\r\n              [size]=\"'20'\"></centric-icon2>\r\n          }\r\n          {{ contentItem.title }}\r\n        </button>\r\n      }\r\n      @for (footerItem of footerMenuItems; track footerItem) {\r\n        <button\r\n          windmill-context-menu-item\r\n          [isSelected]=\"isMenuItemSelected(footerItem)\"\r\n          [isFooter]=\"isMenuItemFooter(footerItem)\"\r\n          [isDisabled]=\"isMenuItemDisabled(footerItem)\"\r\n          [attr.disabled]=\"hasDisabledAttribute(footerItem)\"\r\n          (keydown)=\"onMenuItemKeyDown($event, footerItem)\"\r\n          (click)=\"onMenuItemClick(footerItem)\">\r\n          @if (footerItem.iconId) {\r\n            <centric-icon2\r\n              [svgIcon]=\"footerItem.iconId\"\r\n              [size]=\"'20'\"></centric-icon2>\r\n          }\r\n          {{ footerItem.title }}\r\n        </button>\r\n      }\r\n    </windmill-context-menu-list>\r\n  }\r\n</ng-template>\r\n" }]
        }], propDecorators: { menuItems: [{
                type: Input
            }], contentType: [{
                type: Input
            }], size: [{
                type: Input
            }], disabled: [{
                type: HostBinding,
                args: ['class.is-disabled']
            }, {
                type: Input
            }], dropdownButtonText: [{
                type: Input
            }], dropdownIconId: [{
                type: Input
            }], dropdownType: [{
                type: Input
            }], splitActionsType: [{
                type: Input
            }], splitPersistentType: [{
                type: Input
            }], splitPersistentActiveType: [{
                type: Input
            }], isActive: [{
                type: Input
            }], buttonId: [{
                type: Input
            }], testButtonId: [{
                type: Input
            }], splitArrowButtonId: [{
                type: Input
            }], testSplitArrowButtonId: [{
                type: Input
            }], listId: [{
                type: Input
            }], listDataTestId: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], ariaLabelSplitArrow: [{
                type: Input
            }], ariaLabelledbySplitArrow: [{
                type: Input
            }], ariaDescriptionSplitArrow: [{
                type: Input
            }], ariaDescribedbySplitArrow: [{
                type: Input
            }], actionSelected: [{
                type: Output
            }], openedListChange: [{
                type: Output
            }], menuItemSelected: [{
                type: Output
            }], triggerButton: [{
                type: ViewChild,
                args: ['triggerButton']
            }], contextMenuDirective: [{
                type: ViewChild,
                args: [WindmillContextMenuDirective]
            }] } });

const windmillModules = [CentricIcon2Module, WindmillContextMenuModule];
const moduleComponents = [WindmillComboButtonComponent];
class WindmillComboButtonModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillComboButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: WindmillComboButtonModule, imports: [CommonModule, FormsModule, CentricIcon2Module, WindmillContextMenuModule, WindmillComboButtonComponent], exports: [WindmillComboButtonComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillComboButtonModule, imports: [CommonModule, FormsModule, windmillModules, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillComboButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule, ...windmillModules, ...moduleComponents],
                    exports: [...moduleComponents],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { WindmillComboButtonComponent, WindmillComboButtonModule };
